import {
  createContext,
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type JSX,
} from "react";
import { KeycloakInstance } from "keycloak-js";
import type { NotificationProps } from "@dsaui/components";
import { User } from "lib/models/user";
import { APIClient } from "lib/api";
import { UserProfile } from "lib/api/types";
import {
  AUTHENTICATION_TIMEOUT_MS,
  withAuthenticationTimeout,
} from "lib/authentication";

export interface AppContextProps {
  notification?: NotificationProps | null;
  toggleNotification: (notification: NotificationProps) => void;
  currentUser: User | null;
  ready: boolean;
  authenticated: boolean;
  authenticationError: boolean;
  setCurrentUserProfile: Dispatch<SetStateAction<UserProfile | undefined>>;
  api: APIClient;
  keycloak: KeycloakInstance;
}

export const AppContext = createContext<AppContextProps>({
  ready: false,
  notification: null,
  currentUser: null,
} as unknown as AppContextProps);

export interface AppContextProviderProps {
  keycloak: KeycloakInstance;
  authenticationError?: boolean;
  authenticationTimeoutMs?: number;
  children: JSX.Element | JSX.Element[];
}

const useUserLoader = (
  keycloak: KeycloakInstance,
  initialAuthenticationError = false,
  authenticationTimeoutMs = AUTHENTICATION_TIMEOUT_MS,
): Pick<
  AppContextProps,
  | "currentUser"
  | "setCurrentUserProfile"
  | "ready"
  | "authenticated"
  | "authenticationError"
  | "api"
> => {
  const { authenticated: kcAuthenticated, tokenParsed } = keycloak;

  const kcProfile = useMemo<User["kcProfile"] | undefined>(() => {
    if (!kcAuthenticated) return undefined;
    return {
      id: tokenParsed?.sub,
      username: tokenParsed?.preferred_username,
      email: tokenParsed?.email,
      firstName: tokenParsed?.given_name,
      lastName: tokenParsed?.family_name,
      emailVerified: tokenParsed?.email_verified,
    };
  }, [kcAuthenticated, tokenParsed]);
  const [userProfile, setUserProfile] = useState<User["profile"] | undefined>();
  const api = useMemo<APIClient>(() => new APIClient({ keycloak }), [keycloak]);

  const [userLoadingComplete, setUserLoadingComplete] = useState(
    initialAuthenticationError,
  );
  const [authenticationError, setAuthenticationError] = useState(
    initialAuthenticationError,
  );

  // load api profile one time when ready
  useEffect(() => {
    const loadCurrentUser = async (): Promise<void> => {
      // after kc has initialized, load profile & set userLoaded (app is ready)
      if (!userLoadingComplete) {
        if (!keycloak.authenticated) {
          // User not logged in. Nothing left to do.
          setUserLoadingComplete(true);
        } else {
          // User logged in. Fetching api profile:
          const [err, profile] = await withAuthenticationTimeout(
            api.getProfile(),
            authenticationTimeoutMs,
          ).catch((error: unknown) => {
            console.error(error);
            return [error, null] as const;
          });
          if (err) {
            console.error(err);
            setAuthenticationError(true);
            setUserLoadingComplete(true);
          } else {
            if (!profile) {
              console.error(
                new Error(
                  "AppContextProvider: Profile not found for " + kcProfile?.email,
                ),
              );
              setAuthenticationError(true);
              setUserLoadingComplete(true);
              return;
            }
            setUserProfile(profile);
            setUserLoadingComplete(true);
          }
        }
      }
    };
    loadCurrentUser();
  }, [
    api,
    authenticationTimeoutMs,
    kcProfile,
    kcAuthenticated,
    userLoadingComplete,
    keycloak.authenticated,
  ]);
  const currentUser =
    userProfile && kcProfile
      ? {
          profile: userProfile,
          kcProfile: kcProfile,
        }
      : null;

  const authenticated = currentUser !== null;
  return {
    currentUser,
    ready: userLoadingComplete,
    authenticated,
    authenticationError,
    api,
    setCurrentUserProfile: setUserProfile,
  };
};

const AppContextProvider = ({
  children,
  keycloak,
  authenticationError: initialAuthenticationError = false,
  authenticationTimeoutMs = AUTHENTICATION_TIMEOUT_MS,
}: AppContextProviderProps): JSX.Element => {
  const [notification, setNotification] = useState<NotificationProps>();

  const toggleNotification = useCallback((np: NotificationProps): void => {
    setNotification(np);
  }, []);
  const {
    ready,
    currentUser,
    setCurrentUserProfile,
    authenticated,
    authenticationError,
    api,
  } = useUserLoader(keycloak, initialAuthenticationError, authenticationTimeoutMs);

  const contextValue: AppContextProps = {
    ready,
    notification,
    toggleNotification,
    currentUser,
    authenticated,
    authenticationError,
    setCurrentUserProfile,
    api,
    keycloak,
  };

  return <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>;
};

export default AppContextProvider;
